/**
 * Saved-searches API client — typed fetchers for the user's saved catalog
 * filters. All endpoints are cookie-authenticated under /me/saved-searches.
 *
 * A saved search captures a region code, category slug and/or keyword (no
 * status — the model has no status field). It drives both the in-app saved
 * lists and server-side Telegram alerts.
 */
import { countryConfig } from '../country';
import { apiRequest, apiRaw, NetworkError } from './api';
import { getMetaContext } from './meta-cookies';
import type { ProcurementKind } from './catalog-client';

/** A tender pushed to the user's Telegram as the instant first-match. */
export interface NicheFirstMatch {
  id: string;
  title: string;
  /** Whole so'm as a decimal string, as the catalogue sends it. The API
   *  returns the full tender view; these two feed the onboarding sample's
   *  «💰 · ⏰» line. Optional: absent on older payloads and in mocks. */
  budgetUzs?: string | null;
  /** ISO deadline, or null for a lot without one. */
  deadline?: string | null;
}
/** A sibling niche suggested when the picked niche has no active lots. */
export interface AdjacentNiche {
  code: string;
  name: string;
  tenderCount: number;
}
export interface NicheResult {
  firstMatch: NicheFirstMatch | null;
  adjacentNiches: AdjacentNiche[];
}

export interface SavedSearch {
  id: string;
  name: string;
  regionCode: string | null;
  categorySlug: string | null;
  ikpuCode: string | null;
  nicheSlug: string | null;
  keyword: string | null;
  excludeKeyword: string | null;
  /** Legacy-shaped scalar mirror of the buyer axis (same role as `regionCode`/
   *  `categorySlug`/`ikpuCode`) — `SavedSearch.customerTin` server-side. The
   *  edit sheet falls back to this when `criteria.include.customerTin` is
   *  absent (a row saved before this axis existed had nothing to seed the
   *  blob from either). */
  customerTin: string | null;
  /** Display cache alongside `customerTin` — the buyer's name at save time. */
  customerName: string | null;
  /** Serialized Decimal from the API, or null when no floor is set. */
  minBudgetUzs: string | null;
  /** Serialized Decimal from the API, or null when no ceiling is set. */
  maxBudgetUzs: string | null;
  /**
   * The stored query blob. `exclude.learned` is terms the user ratified from a
   * 👎 in the Telegram digest — NOT part of `excludeKeyword`, since that scalar
   * is rebuilt on every PATCH and would wipe a learned term on an unrelated
   * edit. `exclude.text` is the typed exclusion list (superset of the legacy
   * scalar, which can only hold one term) — authoritative when present.
   *
   * `include.{region,kind,text,ikpu,niche,customerTin}` mirror the same axes
   * the PATCH accepts as `regions`/`kinds`/`textTerms`/`ikpuCodes`/
   * `nicheSlugs`/`customerTins` — this is where the edit sheet seeds them
   * from. `include.category` also exists server-side but is read from the
   * legacy `categorySlug` scalar here instead (see the sheet's own note).
   */
  criteria?: {
    v?: 1;
    include?: {
      category?: string[];
      ikpu?: string[];
      niche?: string[];
      region?: string[];
      kind?: ProcurementKind[];
      text?: string[];
      /** Pinned buyer (заказчик) TIN, as a single-element array — the API
       *  stores one at a time but mirrors the other axes' array shape here.
       *  `[0]` is what `savedSearchToCatalogPath` reads. */
      customerTin?: string[];
    };
    exclude?: { learned?: string[]; text?: string[] };
    /** Budget bounds — set by the /results card (a `results` search); lots
     *  searches keep theirs in the `minBudgetUzs`/`maxBudgetUzs` scalars. */
    budgetUzs?: { min?: number; max?: number };
    /** Relative deadline window, in days. Read only by the `alert=` link. */
    deadlineIn?: { minDays?: number; maxDays?: number };
  } | null;
  /** `lots` (every search the catalogue/bot/onboarding make) or `results`
   *  (the /results card — at most one, see `getResultsSearch`). */
  kind?: 'lots' | 'results';
  /** Каталожный слой this subscription lives in. Not editable — it is what the
   *  search IS — but the preview count needs it, since scope is applied outside
   *  the criteria blob (see `PreviewCountInput.scope`). */
  scope?: string;
  /** Whether this subscription ALSO matches `supply`-scope (cooperation.uz
   *  reverse-auction) leads — opt-in, independent of `scope` itself. Defaults
   *  to `false` server-side (`SavedSearch.includeSupply`'s column default);
   *  optional here only because rows from before the flag existed may still
   *  omit it in a stale cache entry. */
  includeSupply?: boolean;
  active: boolean;
  createdAt: string;
  /**
   * Measured lots/day for this search over the rolling 14-day window, or `null`
   * when it has under a week of history.
   *
   * ⚠️ `null` is NOT zero. Rendering it as «0 в день» tells a subscriber their
   * search matches nothing when the truth is "we have not measured it yet";
   * show «мало данных» instead. Computed server-side by the SAME
   * `matchRatePerDay` the digest picks delivery shapes with.
   */
  rate?: number | null;
  /** What the user PINNED, never the mode the last sweep resolved. A pin the
   *  server has since demoted comes back as `auto`, which is how the toggle
   *  reflects the demotion on the next load. */
  deliveryMode?: 'auto' | 'instant';
  /** Whether «Присылать сразу» may be turned on for this search at all. */
  instantEligible?: boolean;
  /**
   * Why not — the wire CODE, never a sentence: the server serves two languages
   * and the client owns the wording.
   *
   *  - `plan` — free tier, the pooled digest is the upsell surface;
   *  - `broad` — «the whole category», narrows nothing;
   *  - `rate` — over 3 lots/day, too loud for a message per lot;
   *  - `young` — under a week of history, so no rate exists yet.
   */
  instantIneligibleReason?: 'plan' | 'broad' | 'rate' | 'young' | null;
}

export interface CreateSavedSearchInput {
  name: string;
  regionCode?: string;
  /** Several regions at once. Same "plural wins" rule as `ikpuCodes`: the API
   *  lands the full list in `criteria.include.region` and the `regionCode`
   *  column only ever mirrors the first. Sent by the AI proposal, which is
   *  routinely «Ташкент и область». */
  regionCodes?: string[];
  categorySlug?: string;
  ikpuCode?: string;
  /** Slug of the niche (Spec #3 classification layer) this search subscribes
   *  to, e.g. from the niche search-as-you-type. */
  nicheSlug?: string;
  /** Several ИКПУ codes at once. The API's plural form WINS over `ikpuCode`
   *  and lands in `criteria.include.ikpu` in full — the singular column only
   *  ever mirrors the first. Used by the AI proposal, which routinely covers
   *  2–5 codes inside one section. */
  ikpuCodes?: string[];
  /** Several niche slugs at once — same "plural wins" rule as `ikpuCodes`. */
  nicheSlugs?: string[];
  /** Buyer (заказчик) TIN this search is pinned to, from `searchBuyers`
   *  (Task 7). Sent alongside `customerName`, which is display-only. */
  customerTin?: string;
  /** Display name for `customerTin`, so the API/UI never has to resolve a TIN
   *  back to a name — the pick already knows it. */
  customerName?: string;
  keyword?: string;
  /** The free-text terms, when more than one is available at creation time.
   *  They OR each other and AND against every other axis; the API mirrors the
   *  first into the `keyword` column. Matters most for the AI proposal, which
   *  always carries BOTH scripts of every word — joining them into `keyword`
   *  would compile to one phrase and match nothing. */
  textTerms?: string[];
  /** One negative term — a tender matching it is rejected however well it
   *  scores on every other axis. Matched against the same widened haystack as
   *  `keyword` (title + description + AI summaries).
   *  NOTE: not representable in the digest "+N ещё" deep link (the catalog has
   *  no exclusion axis), so that link is approximate for such a search. */
  excludeKeyword?: string;
  /** The negative terms, when more than one is available at creation time —
   *  what the catalog's save popover and the builder both send. Supersedes
   *  `excludeKeyword`, which the API still mirrors from the first entry. */
  excludeTerms?: string[];
  /** Lower budget bound in so'm. Same semantics as the catalog's own
   *  `minBudget`: a set floor EXCLUDES tenders with no disclosed budget. */
  minBudgetUzs?: number;
  /** Upper budget bound in so'm — the ceiling counterpart to `minBudgetUzs`,
   *  same null-exclusion semantics. */
  maxBudgetUzs?: number;
  /** Каталожный слой ('gov' | 'ifi' | 'corporate') this search subscribes
   *  to. Omitted → the API's `SavedSearch.scope` column default (`gov`)
   *  applies, same as every caller that predates this field. */
  scope?: string;
  /** Opt in to ALSO matching `supply`-scope (cooperation.uz reverse-auction)
   *  leads, independent of `scope`. Omitted → the API's `SavedSearch
   *  .includeSupply` column default (`false`) applies. A search saved from
   *  the `/supply` section MUST send `true` explicitly — a supply search
   *  that opts OUT of supply would match nothing at all. */
  includeSupply?: boolean;
  /** Dedup id shared with the browser Pixel ActivationSavedSearch event. */
  metaEventId?: string;
  /** Free-tier "switch niche": replace the existing single niche instead of
   *  hitting the one-niche cap. Only the onboarding wizard sets this. */
  replace?: boolean;
  /**
   * The ИИ-подбор this search was built from (`ai_search_drafts.id`).
   *
   * Optional and server-interpreted: the API closes the draft out itself,
   * writing `outcome = saved` or `edited_saved` (the latter when the saved
   * payload differs from what the model proposed) plus `savedSearchId`. The
   * client therefore does NOT report the outcome separately on this path —
   * doing both would produce two writes racing for the same row.
   *
   * ⚠️ The create DTO runs with `forbidNonWhitelisted`, so this field only
   * works once the API side of the contract has landed; until then a request
   * carrying it is refused with 400, not ignored.
   */
  aiDraftId?: string;
}

/** GET /me/saved-searches — the current user's active saved searches. */
export async function listSavedSearches(): Promise<SavedSearch[]> {
  return apiRequest<SavedSearch[]>('/me/saved-searches');
}

/**
 * The reader's ONE `results` saved search (the /results card, saved), or
 * `null`. Kept off `/me/saved-searches` on purpose: that list feeds the
 * sidebar, «Мои тендеры», onboarding and the free 3-search cap, all of which
 * mean «lots searches». Same delivery fields (`rate`, `deliveryMode`,
 * `instantEligible`) as a lots search.
 */
export async function getResultsSearch(): Promise<SavedSearch | null> {
  return (await apiRequest<SavedSearch | null>('/me/results-search')) ?? null;
}

export interface SaveResultsSearchResult {
  data: SavedSearch;
  /** `false` — an existing results search was UPDATED (one per user). */
  created: boolean;
  /** Axes the update moved (`text`, `category`, `region`, …). */
  changed: string[];
}

/** PUT /me/results-search — create the results search or update the one the
 *  reader has. Not subject to the free saved-search cap. */
export async function saveResultsSearch(input: {
  criteria: object;
  /** The reader's language — the API names the search «Результаты» /
   *  «Natijalar» by it (a fixed name, owner 2026-10-07; never user-chosen). */
  lang?: string;
}): Promise<SaveResultsSearchResult> {
  return apiRaw<SaveResultsSearchResult>('/me/results-search', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** POST /me/saved-searches — persist the given filter snapshot. */
export async function createSavedSearch(input: CreateSavedSearchInput): Promise<SavedSearch> {
  // Attach Meta context (fbp/fbc/utm/eventSourceUrl) so the server-side
  // ActivationSavedSearch CAPI event dedups against the browser Pixel event.
  return apiRequest<SavedSearch>('/me/saved-searches', {
    method: 'POST',
    body: JSON.stringify({ ...input, ...getMetaContext() }),
  });
}

/** Like createSavedSearch but returns the instant first-match + empty-state
 *  adjacent niches (the onboarding "WOW in 60s" step). Uses the raw envelope so
 *  the sibling fields survive the `{ data }` unwrap. */
/**
 * Retries a save that never reached a response.
 *
 * ONLY on {@link NetworkError} — a server that answered "no" (ApiError: over the
 * free cap, unauthorised) must not be asked again. A transport failure settles
 * nothing, and on mobile it is the common case: `/api/catalog` alone logged 13
 * client-aborted requests in a single day on prod, and one onboarding save was
 * lost that way, leaving the user staring at «Failed to fetch».
 *
 * Safe to repeat because onboarding always sends `replace: true`, and the API
 * deactivates the existing active search before creating on the Free tier — so
 * a duplicate attempt still ends with exactly one saved niche. (A Pro account
 * ignores `replace`, so a retry there could add a second niche; that is a
 * visible, user-removable row, against the alternative of silently discarding
 * the choice of every user on a flaky connection.)
 */
async function retryOnNetworkFailure<T>(attempt: () => Promise<T>, tries = 3): Promise<T> {
  let last: unknown;
  for (let i = 0; i < tries; i++) {
    try {
      return await attempt();
    } catch (e) {
      if (!(e instanceof NetworkError)) throw e;
      last = e;
      // Short and fixed: a mobile blip clears in well under a second, and a
      // long backoff would outlast the user's patience for a tap they already
      // made.
      if (i < tries - 1) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw last;
}

export async function createNiche(
  input: CreateSavedSearchInput,
): Promise<{ search: SavedSearch } & NicheResult> {
  const body = await retryOnNetworkFailure(() =>
    apiRaw<{ data: SavedSearch } & NicheResult>('/me/saved-searches', {
      method: 'POST',
      body: JSON.stringify({ ...input, ...getMetaContext() }),
    }),
  );
  return {
    search: body.data,
    firstMatch: body.firstMatch ?? null,
    adjacentNiches: body.adjacentNiches ?? [],
  };
}

/** POST /me/first-alert — send the instant first-match for the newest niche now
 *  (called after the Telegram step, once a delivery channel exists). */
export async function sendFirstAlert(): Promise<NicheResult> {
  const body = await apiRaw<NicheResult>('/me/first-alert', { method: 'POST' });
  return { firstMatch: body.firstMatch ?? null, adjacentNiches: body.adjacentNiches ?? [] };
}

/** DELETE /me/saved-searches/:id — soft-delete a saved search (204). */
export async function deleteSavedSearch(id: string): Promise<void> {
  await apiRequest<void>(`/me/saved-searches/${id}`, { method: 'DELETE' });
}

/** Editable subset of a saved search. `null` CLEARS a lever; an omitted key
 *  leaves it untouched — the two must stay distinguishable, or clearing one
 *  would silently wipe the other. */
export interface UpdateSavedSearchInput {
  name?: string;
  minBudgetUzs?: number | null;
  /** `null` clears the ceiling. The API rejects (400) a ceiling that would
   *  end up below the floor. */
  maxBudgetUzs?: number | null;
  excludeKeyword?: string | null;
  /** Typed exclusions as they should END UP. Supersedes `excludeKeyword`,
   *  which cannot express more than one term. */
  excludeTerms?: string[];
  /** Learned exclusions as they should END UP. Send the remaining list to drop
   *  one; terms are only ever ADDED by accepting a proposal in the bot. */
  learnedExclusions?: string[];
  /** Categories this subscription covers, as they should END UP. Part of the
   *  SUBJECT axis alongside `ikpuCodes`/`nicheSlugs` — the API unions all
   *  three, so clearing one is only refused when the other two are ALSO
   *  ending up empty in the same patch. */
  categorySlugs?: string[];
  /** ИКПУ codes this subscription covers, as they should END UP. */
  ikpuCodes?: string[];
  /** Niche slugs this subscription covers, as they should END UP. */
  nicheSlugs?: string[];
  /** Region codes as they should END UP. */
  regions?: string[];
  /** Procurement kinds as they should END UP. */
  kinds?: ProcurementKind[];
  /** Free-text terms as they should END UP. */
  textTerms?: string[];
  /** Opt in/out of also matching `supply`-scope (cooperation.uz) leads.
   *  Omitted leaves the flag untouched, same as every other field here. */
  includeSupply?: boolean;
  /** Pinned buyer (заказчик) TINs as they should END UP — `[]` clears the
   *  pin, same "present replaces, omitted leaves alone" rule as every other
   *  axis here. The API stores a single TIN, but this mirrors the plural
   *  spelling the other `*s` fields use for a "replace wholesale" patch. */
  customerTins?: string[];
  /** Display name for the pinned buyer, kept in step with `customerTins`. */
  customerName?: string;
  /**
   * The «Присылать сразу» pin. `auto` is always accepted; `instant` on an
   * ineligible search is refused with 400 and an `instant_ineligible_<reason>`
   * code, so the UI must gate the control rather than relying on the write.
   */
  deliveryMode?: 'auto' | 'instant';
}

/**
 * PATCH /me/saved-searches/:id — refine an existing search in place.
 *
 * In place matters: before this existed the only way to change a budget floor
 * was delete-and-recreate, which on the free tier hits the one-search cap and
 * discards the search's match history.
 */
export async function updateSavedSearch(
  id: string,
  patch: UpdateSavedSearchInput,
): Promise<SavedSearch> {
  return apiRequest<SavedSearch>(`/me/saved-searches/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * PATCH /me/notifications — flip the master on/off switch for all saved-search
 * + supplier-match alerts (Telegram and email). Returns the new state.
 */
export async function updateNotificationsEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
  return apiRequest<{ enabled: boolean }>('/me/notifications', {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

/** Per-channel notification state returned by GET/PATCH /me/notifications. */
export interface ChannelNotifications {
  /** Master gate (email || telegram) — kept coherent server-side. */
  enabled: boolean;
  emailNotificationsEnabled: boolean;
  telegramNotificationsEnabled: boolean;
}

/**
 * PATCH /me/notifications — flip email and/or Telegram alerts independently. A
 * single-channel patch leaves the other channel untouched; the server recomputes
 * the master (email || telegram) and returns the full new state.
 */
export async function updateChannelNotifications(patch: {
  emailNotificationsEnabled?: boolean;
  telegramNotificationsEnabled?: boolean;
}): Promise<ChannelNotifications> {
  return apiRequest<ChannelNotifications>('/me/notifications', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * How many veto words the catalog's `exclude` axis accepts, and how long the
 * whole comma-joined value may be.
 *
 * Both mirror the API exactly — `MAX_EXCLUDE_TERMS` in `catalog.service.ts` and
 * `@MaxLength(256)` on `ListTendersDto.exclude` — so a link built here is never
 * itself the reason the catalog answers 400. The service truncates past the term
 * cap silently; the DTO does NOT truncate an over-long string, it rejects the
 * request, and the whole list is replaced by the error card.
 */
const CATALOG_EXCLUDE_MAX_TERMS = 10;
const CATALOG_EXCLUDE_MAX_CHARS = 256;

/**
 * The veto terms the search's ALERT actually applies, as the catalog's
 * comma-separated `exclude` value — or null when it vetoes nothing.
 *
 * Typed AND learned, in that order, because that is precisely what the sweep
 * compiles: `compileCriteria` (`services/api/src/modules/catalog/criteria.ts`)
 * pushes `excludeText(term)` for every `exclude.text` entry and then for every
 * `exclude.learned` one, under its own note that «typed and learned terms veto
 * identically — the split is provenance, not semantics». Carrying only the typed
 * half would leave the catalogue with a weaker veto than the digest, which is
 * the same class of divergence this link exists to close.
 *
 * `excludeKeyword` is the legacy single-value spelling of the same thing and is
 * read only when the blob carries no `text` — the dual-read `criteriaForSearch`
 * performs server-side, not a second source of truth.
 *
 * A term containing a comma is DROPPED rather than escaped: `URLSearchParams`
 * encodes the value but the catalog splits on the literal comma, so such a term
 * would arrive as two different vetoes and exclude more than was asked. Same
 * rule, same reason as `buildSavedSearchCatalogUrl` in `digest-links.ts`.
 */
function catalogExcludeParam(s: SavedSearch): string | null {
  const blobText = s.criteria?.exclude?.text ?? [];
  const typed =
    blobText.length > 0 ? blobText : s.excludeKeyword ? [s.excludeKeyword] : ([] as string[]);
  const seen = new Set<string>();
  let joined = '';
  let count = 0;
  for (const raw of [...typed, ...(s.criteria?.exclude?.learned ?? [])]) {
    const term = raw.trim();
    if (!term || term.includes(',')) continue;
    // Case-insensitive dedup. A term ratified from a 👎 in the digest can repeat
    // one the user typed, and two identical vetoes narrow nothing while eating
    // one of the ten slots a real second word needed.
    const key = term.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const next = joined ? `${joined},${term}` : term;
    // The length cap is measured on the JOINED value, which is what the DTO's
    // `MaxLength` sees. Stopping at the last term that fits keeps a valid prefix
    // of the veto instead of dropping the axis wholesale.
    if (next.length > CATALOG_EXCLUDE_MAX_CHARS) break;
    joined = next;
    if (++count === CATALOG_EXCLUDE_MAX_TERMS) break;
  }
  return joined || null;
}

/** A budget bound as the catalog's own `minBudget`/`maxBudget` spelling, or null.
 *  The wire type is a serialized Decimal, so it is a string here. */
function catalogBudgetParam(value: string | null): string | null {
  if (value === null) return null;
  const n = Number(value);
  // `>= 0`, not `> 0`: a floor of 0 is a real filter — it excludes lots with no
  // disclosed budget — and `compileCriteria` applies it exactly that way, so
  // dropping it would make the page show lots the alert never matched.
  return Number.isFinite(n) && n >= 0 ? String(Math.trunc(n)) : null;
}

/** Longest `alert=` value — `ALERT_PARAM_MAX_CHARS` in the API
 *  (`services/api/src/modules/catalog/alert-param-limit.ts`). */
const ALERT_PARAM_MAX_CHARS = 6000;

/**
 * The search's WHOLE definition as the catalogue's `alert=` value (Spain,
 * `features.alertCriteriaLink`), or null when it has no criteria blob to carry
 * (the caller keeps the scalar link).
 *
 * The scalar link below holds the first code, the first word and the category,
 * and the catalogue intersects them where the alert unions — measured 22.10 on
 * MiLicita, an alert of 10 lots opened a list of 0, one of 1 130 a list of 14.
 * `alert=` is compiled server-side by the alert sweep's own `compileCriteria`,
 * so the list and the alert are one predicate (`alert-criteria.ts`; the digest
 * link is built the same way in `digest-links.ts`).
 *
 * `scope` stays out (the path carries the layer). A value past the API's cap
 * loses its WORDS first — the list then shows more than the alert, never less.
 */
export function savedSearchAlertParam(s: SavedSearch): string | null {
  const c = s.criteria;
  if (!c) return null;
  const blob: Record<string, unknown> = { v: 1 };
  if (c.include && Object.keys(c.include).length > 0) blob.include = c.include;
  if (c.exclude && Object.keys(c.exclude).length > 0) blob.exclude = c.exclude;
  if (c.budgetUzs) blob.budgetUzs = c.budgetUzs;
  if (c.deadlineIn) blob.deadlineIn = c.deadlineIn;
  if (Object.keys(blob).length === 1) return null;
  const value = JSON.stringify(blob);
  if (value.length <= ALERT_PARAM_MAX_CHARS) return value;
  if (!c.include?.text) return null;
  const { text: _words, ...rest } = c.include;
  const narrower: Record<string, unknown> = { ...blob };
  if (Object.keys(rest).length > 0) narrower.include = rest;
  else delete narrower.include;
  // Words were all it narrowed by: no `alert=` at all, the scalar link instead.
  if (Object.keys(narrower).length === 1) return null;
  const shorter = JSON.stringify(narrower);
  return shorter.length <= ALERT_PARAM_MAX_CHARS ? shorter : null;
}

/**
 * Build the catalog URL that re-applies a saved search's filters.
 *
 * Maps regionCode → `region`, categorySlug → `category`, ikpuCode → `ikpu`,
 * nicheSlug → `niche`, the free-text axis → `q`, the veto axis → `exclude`,
 * criteria.include.customerTin[0] → `customer`, a single criteria.include.kind →
 * `kind` and the budget bounds → `minBudget`/`maxBudget`, omitting any that are
 * null/empty. Bare `/catalog` when nothing is set.
 *
 * ⚠️ The link is an APPROXIMATION in one direction only — it may show more than
 * the alert matches, never less. See the `q` and `kind` notes below for the two
 * axes where the catalog's URL cannot express what the criteria blob holds.
 */
export function savedSearchToCatalogPath(s: SavedSearch): string {
  // Spain: the whole search as one `alert=` param — see `savedSearchAlertParam`.
  const alert = countryConfig.features.alertCriteriaLink === true ? savedSearchAlertParam(s) : null;
  if (alert) return `/catalog?${new URLSearchParams({ alert }).toString()}`;
  const params = new URLSearchParams();
  if (s.regionCode) params.set('region', s.regionCode);
  if (s.categorySlug) params.set('category', s.categorySlug);
  if (s.ikpuCode) params.set('ikpu', s.ikpuCode);
  if (s.nicheSlug) params.set('niche', s.nicheSlug);
  /*
   * The free-text axis, as `q` — ONE term, never the joined list.
   *
   * That is not an oversight. A saved search ORs its terms: `compileCriteria`
   * pushes a SINGLE `{ OR: textTerms.flatMap(textOr) }`, under a comment saying
   * its help text «promises any one word is enough». The catalog's `q` is the
   * opposite — `plainto_tsquery` ANDs every word it is handed
   * (`services/api/src/modules/search/search-text.ts`), and the thin-query
   * recall arms widen spelling, not the conjunction. So joining
   * «трансформатор transformator» would ask for lots carrying BOTH spellings
   * and hand back an empty list where the alert matches hundreds — a wronger
   * answer than a narrower one.
   *
   * `keyword` first because it IS the mirror of `include.text[0]`: the API
   * writes `data.keyword = trimmedTextTerms[0]` on every patch
   * (`saved-search.repository.ts`), so the two agree, and the blob is only read
   * for a row whose column was never mirrored. Either way the link under-shows
   * rather than over-shows — the safe direction that file documents for this
   * mirror.
   */
  const text = s.keyword ?? s.criteria?.include?.text?.[0] ?? null;
  if (text) params.set('q', text);
  // Only in `criteria` — unlike region/ikpu/niche, the buyer pin has no
  // legacy scalar column to fall back to (it's a Task 8-era axis).
  const customerTin = s.criteria?.include?.customerTin?.[0];
  if (customerTin) params.set('customer', customerTin);
  /*
   * Procurement kind — only when the search pins exactly ONE.
   *
   * The axis is a list server-side (`include.kind`, and the edit sheet's chip
   * strip is multi-select) while the catalog's param takes a single value
   * (`@IsIn(PROCUREMENT_KINDS)`). Emitting the first of two would hide a whole
   * third of the subscription behind a filter the user never set on the page,
   * so a multi-kind search leaves the axis off — showing more than the alert,
   * not less.
   */
  const kinds = s.criteria?.include?.kind ?? [];
  const kind = kinds.length === 1 ? kinds[0] : undefined;
  if (kind) params.set('kind', kind);
  /*
   * Budget bounds, read from the scalar columns rather than the blob: the API
   * writes both on create and on every patch, they are what the edit sheet
   * seeds its own fields from, and the digest's deep link
   * (`digest-links.ts`) is built from the same column — one spelling, not two.
   */
  const minBudget = catalogBudgetParam(s.minBudgetUzs);
  if (minBudget !== null) params.set('minBudget', minBudget);
  const maxBudget = catalogBudgetParam(s.maxBudgetUzs);
  if (maxBudget !== null) params.set('maxBudget', maxBudget);
  // The negative axis. Last because it is the one the catalog gained most
  // recently — and the one whose absence here made a minus word look broken.
  const exclude = catalogExcludeParam(s);
  if (exclude) params.set('exclude', exclude);
  const qs = params.toString();
  return qs ? `/catalog?${qs}` : '/catalog';
}

/** The criteria the builder would save, for a live match count. */
export interface PreviewCountInput {
  categorySlugs?: string[];
  ikpuCodes?: string[];
  nicheSlugs?: string[];
  regions?: string[];
  kinds?: string[];
  textTerms?: string[];
  excludeTerms?: string[];
  /** Pinned buyer (заказчик) TIN — mirrors `CreateSavedSearchInput.customerTin`
   *  so the preview count reflects the same buyer pin the save would send. */
  customerTin?: string;
  minBudgetUzs?: number;
  maxBudgetUzs?: number;
  /**
   * The каталожный слой of the search being edited. NOT part of the criteria
   * the builder edits — scope is what a subscription IS, and the sheet cannot
   * change it — but the count is wrong without it: an `ifi` subscription's
   * preview counted gov lots and reported a number it would never deliver.
   * The server defaults it to `gov`, matching the column default.
   */
  scope?: string;
  /** Same opt-in as `SavedSearch.includeSupply` — must be sent when the
   *  builder's current form state has it on, or the number shown counts a
   *  smaller set than the search would actually match once saved. Server
   *  defaults it to `false`, matching the column default. */
  includeSupply?: boolean;
}

/**
 * How many LIVE tenders these criteria match right now, before saving.
 *
 * The server compiles this through the same `compileCriteria` the alert sweep
 * uses, so the number and the alert cannot describe different queries. It
 * counts open tenders (active + closing) — it is not a forecast of how many
 * alerts will arrive, which depends on what gets published from here on.
 */
export async function previewSavedSearchCount(input: PreviewCountInput): Promise<number> {
  const res = await apiRequest<{ count: number }>('/me/saved-searches/preview-count', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res?.count ?? 0;
}
